import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  Input,
  inject,
  signal,
} from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { Subscription } from 'rxjs'
import { TranslationService } from '../../../services/translation.service'
import { ProductImageService } from '../../../services/product-image.service'

@Component({
  selector: 'app-product-image',
  imports: [MatIconModule],
  // Avoid checking every photo during unrelated page updates.
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Let screen readers know when the photo is still loading.
  host: {
    '[attr.aria-busy]': "state() === 'loading' ? 'true' : null",
  },
  templateUrl: './product-image.component.html',
  styleUrl: './product-image.component.scss',
})
export class ProductImageComponent implements OnChanges, OnDestroy {
  @Input() picture: string | null | undefined
  @Input() name = ''
  @Input() loading: 'lazy' | 'eager' = 'lazy'
  @Input() size?: 'card' | 'detail'
  @Input() compact = false
  @Input() revision = 0
  readonly t = inject(TranslationService)
  readonly source = signal<string | undefined>(undefined)
  readonly state = signal<'loading' | 'loaded' | 'unavailable'>('unavailable')
  private images = inject(ProductImageService)
  private element = inject<ElementRef<HTMLElement>>(ElementRef)
  private request?: Subscription
  private objectUrl?: string
  private observer?: IntersectionObserver

  /**
   * Starts image loading when the photo is visible or marked as eager.
   * @returns Nothing; resets the previous photo and schedules the new one.
   */
  ngOnChanges(): void {
    this.clear()
    const picture = this.picture
    if (!picture) return
    this.state.set('loading')
    if (
      this.loading === 'lazy' &&
      typeof IntersectionObserver !== 'undefined'
    ) {
      // IntersectionObserver lets the browser tell us when this photo approaches
      // the visible part of the page, instead of downloading every photo at once.
      // The 200px margin below starts loading just before it scrolls into view.
      const observer = new IntersectionObserver(
        (entries) => {
          // Ignore an old photo's callback or a photo still outside that area.
          if (
            this.observer !== observer ||
            !entries.some((entry) => entry.isIntersecting)
          )
            return
          // We only need one download, so stop watching before loading the photo.
          observer.disconnect()
          this.observer = undefined
          this.load(picture)
        },
        { rootMargin: '200px' },
      )
      this.observer = observer
      observer.observe(this.element.nativeElement)
    } else {
      this.load(picture)
    }
  }

  /**
   * Shows the photo once the browser has decoded it.
   * @returns Nothing; marks the photo as loaded.
   */
  loaded(): void {
    this.state.set('loaded')
  }

  /**
   * Leaves the placeholder after a failed request or decode, without retries.
   * @returns Nothing; clears the photo and releases its object URL.
   */
  failed(): void {
    this.state.set('unavailable')
    this.source.set(undefined)
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = undefined
  }

  /**
   * Releases downloads, visibility observers and object URLs when removed.
   * @returns Nothing; cleans up the component's image resources.
   */
  ngOnDestroy(): void {
    this.clear()
  }

  /**
   * Makes the authorized response available to the browser for decoding.
   * @param picture Image address returned by the API.
   * @returns Nothing; updates the photo source when the download completes.
   */
  private load(picture: string): void {
    this.request = this.images.load(picture, this.size).subscribe({
      next: (blob) => {
        try {
          // Fetch with the auth header first, then give the image a local blob URL.
          this.objectUrl = URL.createObjectURL(blob)
          this.source.set(this.objectUrl)
        } catch {
          // If the browser cannot create a preview, leave the placeholder.
          this.failed()
        }
      },
      error: () => this.failed(),
    })
  }

  /**
   * Clears the previous image before a source change or component removal.
   * @returns Nothing; cancels pending work and restores the placeholder.
   */
  private clear(): void {
    this.observer?.disconnect()
    this.observer = undefined
    this.request?.unsubscribe()
    this.failed()
  }
}
